import React, { useEffect, useRef, useState, useCallback } from 'react';

// ── PaintInterior — 페인트 캐치 게임 (Catch the falling) ───────
// 페인트 롤러로 떨어지는 페인트통 받기. 도구는 보너스, 깨진 거울은 감점.
// 페인트통 3번 놓치면 게임 오버.

const W = 360;
const H = 520;
const GRAV = 0.16;
const PLAYER_W = 60;
const PLAYER_Y = H - 70;
const ITEM_R = 22;
const BEST_KEY = 'ddabak_egg_best_interior';

const PAINTS = ['🪣','🎨','🖌️'];      // good (+1)
const TOOLS  = ['🔨','🧰','🪜','🔩']; // bonus (+5)
const BADS   = ['🪞','💥','🧱'];      // bad (-10 + 차감)
const PLAYER = '🎨';                   // 플레이어 (페인트 롤러)

// 페인트 롤러 커서 (게임 영역에서)
const ROLLER_CURSOR = `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32'><text x='16' y='24' font-size='26' text-anchor='middle'>%F0%9F%8E%A8</text></svg>") 16 16, pointer`;

function pick(arr) { return arr[Math.floor(Math.random() * arr.length)]; }

function drawEmoji(ctx, emoji, x, y, size = 36, rot = 0) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rot);
  ctx.font = `${size}px serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(emoji, 0, 0);
  ctx.restore();
}

export default function PaintInterior({ onClose }) {
  const canvasRef = useRef(null);
  const stateRef  = useRef(null);
  const rafRef    = useRef(null);
  const [phase, setPhase] = useState('idle');   // idle | playing | dead
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [best,  setBest]  = useState(
    () => parseInt(localStorage.getItem(BEST_KEY) || '0')
  );

  const initState = useCallback(() => ({
    items: [],
    particles: [],
    player: { x: W / 2, targetX: W / 2 },
    frame: 0,
    score: 0,
    lives: 3,
    spawnTimer: 0,
    keyL: false,
    keyR: false,
  }), []);

  function spawnItem(s) {
    const r = Math.random();
    let type, emoji;
    if (r < 0.7)      { type = 'paint'; emoji = pick(PAINTS); }
    else if (r < 0.88){ type = 'tool';  emoji = pick(TOOLS); }
    else              { type = 'bad';   emoji = pick(BADS); }

    s.items.push({
      id: Math.random(),
      x: 30 + Math.random() * (W - 60),
      y: -30,
      vy: 1.6 + Math.random() * 0.8,
      rot: 0,
      vrot: (Math.random() - 0.5) * 0.06,
      type, emoji,
    });
  }

  function spawnParticles(arr, x, y, color, text) {
    for (let i = 0; i < 6; i++) {
      arr.push({
        x, y,
        vx: (Math.random() - 0.5) * 4,
        vy: -1 - Math.random() * 3,
        life: 1,
        color, text,
      });
    }
  }

  const start = useCallback(() => {
    if (phase === 'idle' || phase === 'dead') {
      stateRef.current = initState();
      setScore(0); setLives(3);
      setPhase('playing');
    }
  }, [phase, initState]);

  // 좌/우 키보드
  useEffect(() => {
    const onKey = (e) => {
      if (e.code === 'Space' && (phase === 'idle' || phase === 'dead')) {
        e.preventDefault(); start(); return;
      }
      if (!stateRef.current) return;
      if (e.type === 'keydown') {
        if (e.code === 'ArrowLeft')  stateRef.current.keyL = true;
        if (e.code === 'ArrowRight') stateRef.current.keyR = true;
      } else {
        if (e.code === 'ArrowLeft')  stateRef.current.keyL = false;
        if (e.code === 'ArrowRight') stateRef.current.keyR = false;
      }
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup',   onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keyup',   onKey);
    };
  }, [phase, start]);

  // 마우스 / 터치 — 위치 추적
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const updateTarget = (clientX) => {
      const rect = canvas.getBoundingClientRect();
      const x = (clientX - rect.left) * (W / rect.width);
      if (stateRef.current) stateRef.current.player.targetX = x;
    };

    const onMove = (e) => {
      if (phase !== 'playing') return;
      e.preventDefault();
      const evt = e.touches ? e.touches[0] : e;
      updateTarget(evt.clientX);
    };
    const onClickOrTap = (e) => {
      if (phase === 'idle' || phase === 'dead') {
        e.preventDefault(); start(); return;
      }
      const evt = e.touches ? e.touches[0] : e;
      updateTarget(evt.clientX);
    };

    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('click',     onClickOrTap);
    canvas.addEventListener('touchstart', onClickOrTap, { passive: false });
    canvas.addEventListener('touchmove',  onMove,       { passive: false });
    return () => {
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('click',     onClickOrTap);
      canvas.removeEventListener('touchstart', onClickOrTap);
      canvas.removeEventListener('touchmove',  onMove);
    };
  }, [phase, start]);

  // 게임 루프
  useEffect(() => {
    if (!stateRef.current) stateRef.current = initState();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    let currentPhase = phase;

    function loop() {
      const s = stateRef.current;
      ctx.clearRect(0, 0, W, H);

      // 배경 (베이지·우드톤)
      const bg = ctx.createLinearGradient(0, 0, 0, H);
      bg.addColorStop(0, '#F4F4F5');
      bg.addColorStop(1, '#fde68a');
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, W, H);

      // 마룻바닥 (우드 줄무늬)
      ctx.fillStyle = '#52525B';
      ctx.fillRect(0, H - 40, W, 40);
      ctx.fillStyle = '#78350f';
      for (let gx = 0; gx < W; gx += 60) {
        ctx.fillRect(gx, H - 40, 2, 40);
      }
      ctx.fillStyle = '#a16207';
      ctx.fillRect(0, H - 40, W, 3);

      // 벽 패턴 (배경 텍스처)
      ctx.fillStyle = 'rgba(180, 83, 9, 0.06)';
      for (let y = 60; y < H - 60; y += 80) {
        ctx.fillRect(0, y, W, 1);
      }

      if (currentPhase === 'playing') {
        s.frame++;

        // 키보드 입력 → targetX 이동
        if (s.keyL) s.player.targetX = Math.max(PLAYER_W / 2, s.player.targetX - 6);
        if (s.keyR) s.player.targetX = Math.min(W - PLAYER_W / 2, s.player.targetX + 6);

        // 부드러운 이동
        const dx = s.player.targetX - s.player.x;
        s.player.x += dx * 0.22;

        // 객체 생성 (난이도 점차 ↑)
        s.spawnTimer++;
        const interval = Math.max(28, 70 - Math.floor(s.frame / 360) * 5);
        if (s.spawnTimer >= interval) {
          s.spawnTimer = 0;
          spawnItem(s);
          if (s.frame > 720 && Math.random() < 0.2) spawnItem(s);
        }

        // 객체 이동 + 충돌
        for (let i = s.items.length - 1; i >= 0; i--) {
          const it = s.items[i];
          it.vy += GRAV * 0.5;
          it.y += it.vy;
          it.rot += it.vrot;

          // 플레이어와 충돌 체크 (사각형 영역)
          const inX = Math.abs(it.x - s.player.x) < PLAYER_W / 2 + ITEM_R - 4;
          const inY = it.y > PLAYER_Y - 24 && it.y < PLAYER_Y + 8;
          if (inX && inY) {
            if (it.type === 'paint') {
              s.score += 1;
              setScore(s.score);
              spawnParticles(s.particles, it.x, it.y, '#52525B', '+1');
            } else if (it.type === 'tool') {
              s.score += 5;
              setScore(s.score);
              spawnParticles(s.particles, it.x, it.y, '#0f766e', '+5');
            } else { // bad
              s.score = Math.max(0, s.score - 10);
              setScore(s.score);
              s.lives--;
              setLives(s.lives);
              spawnParticles(s.particles, it.x, it.y, '#b91c1c', '-10');
              if (s.lives <= 0) {
                currentPhase = 'dead';
                setPhase('dead');
              }
            }
            s.items.splice(i, 1);
            continue;
          }

          // 화면 밖 (놓침)
          if (it.y > H) {
            if (it.type === 'paint') {
              // 페인트통만 놓침 카운트
              s.lives--;
              setLives(s.lives);
              spawnParticles(s.particles, it.x, H - 50, '#9ca3af', '놓침');
              if (s.lives <= 0) {
                currentPhase = 'dead';
                setPhase('dead');
              }
            }
            s.items.splice(i, 1);
          }
        }

        // 파티클
        for (let i = s.particles.length - 1; i >= 0; i--) {
          const p = s.particles[i];
          p.x += p.vx; p.y += p.vy; p.vy += 0.18; p.life -= 0.025;
          if (p.life <= 0) s.particles.splice(i, 1);
        }

        if (currentPhase === 'dead') {
          setBest(prev => {
            const next = Math.max(prev, s.score);
            localStorage.setItem(BEST_KEY, String(next));
            return next;
          });
        }
      }

      // 객체 그리기
      s.items.forEach(it => drawEmoji(ctx, it.emoji, it.x, it.y, 38, it.rot));

      // 플레이어 (페인트 롤러)
      ctx.save();
      // 그림자
      ctx.fillStyle = 'rgba(0,0,0,0.18)';
      ctx.beginPath();
      ctx.ellipse(s.player.x, PLAYER_Y + 22, PLAYER_W / 2, 6, 0, 0, Math.PI * 2);
      ctx.fill();
      // 손잡이 (수직 막대)
      ctx.fillStyle = '#7c2d12';
      ctx.fillRect(s.player.x - 3, PLAYER_Y - 32, 6, 36);
      // 롤러 본체 (가로 원통)
      ctx.fillStyle = '#A1A1AA';
      ctx.beginPath();
      ctx.roundRect(s.player.x - PLAYER_W / 2, PLAYER_Y - 8, PLAYER_W, 16, 8);
      ctx.fill();
      ctx.fillStyle = '#EA580C';
      ctx.beginPath();
      ctx.roundRect(s.player.x - PLAYER_W / 2 + 3, PLAYER_Y - 5, PLAYER_W - 6, 10, 5);
      ctx.fill();
      // 페인트 자국 (롤러 뒤로)
      ctx.fillStyle = 'rgba(180, 83, 9, 0.18)';
      ctx.fillRect(s.player.x - PLAYER_W / 2, PLAYER_Y + 12, PLAYER_W, 4);
      ctx.restore();

      // 파티클
      s.particles.forEach(p => {
        ctx.globalAlpha = p.life;
        ctx.fillStyle = p.color;
        ctx.font = 'bold 14px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(p.text, p.x, p.y);
        ctx.globalAlpha = 1;
      });

      // UI: 점수 (우상단)
      ctx.fillStyle = 'rgba(0,0,0,0.2)';
      ctx.font = 'bold 30px sans-serif';
      ctx.textAlign = 'right';
      ctx.fillText(String(s.score), W - 18, 42);
      ctx.fillStyle = '#7c2d12';
      ctx.fillText(String(s.score), W - 20, 40);

      // UI: 라이프 (좌상단)
      ctx.font = '20px serif';
      ctx.textAlign = 'left';
      for (let i = 0; i < 3; i++) {
        ctx.globalAlpha = i < s.lives ? 1 : 0.25;
        ctx.fillText('🪣', 18 + i * 26, 50);
      }
      ctx.globalAlpha = 1;

      // 시작 화면
      if (currentPhase === 'idle') {
        ctx.fillStyle = 'rgba(0,0,0,0.4)';
        ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 32px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('🔨 셈셈셈!', W / 2, H / 2 - 60);
        ctx.font = 'bold 22px sans-serif';
        ctx.fillStyle = '#fde68a';
        ctx.fillText('견적 따자!', W / 2, H / 2 - 28);
        ctx.font = '16px sans-serif';
        ctx.fillStyle = '#fff';
        ctx.fillText('롤러로 페인트통을 받으세요', W / 2, H / 2 + 14);
        ctx.font = '13px sans-serif';
        ctx.fillStyle = 'rgba(255,255,255,0.7)';
        ctx.fillText('🪣 페인트 +1   🔨 도구 +5   🪞 거울 -10', W / 2, H / 2 + 42);
        ctx.fillStyle = 'rgba(255,255,255,0.5)';
        ctx.font = '12px sans-serif';
        ctx.fillText('마우스/터치로 좌우 이동, 화살표 키도 가능', W / 2, H / 2 + 70);
      }

      // 게임 오버 카드
      if (currentPhase === 'dead') {
        ctx.fillStyle = 'rgba(0,0,0,0.55)';
        ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.roundRect(W / 2 - 130, H / 2 - 110, 260, 220, 20);
        ctx.fill();

        ctx.fillStyle = '#7c2d12';
        ctx.font = 'bold 26px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('공정 종료 🔨', W / 2, H / 2 - 65);

        ctx.fillStyle = '#374151';
        ctx.font = 'bold 14px sans-serif';
        ctx.fillText('점수', W / 2 - 55, H / 2 - 28);
        ctx.fillText('최고', W / 2 + 55, H / 2 - 28);

        ctx.font = 'bold 36px sans-serif';
        ctx.fillStyle = '#7c2d12';
        ctx.fillText(String(s.score), W / 2 - 55, H / 2 + 14);
        ctx.fillStyle = '#EA580C';
        ctx.fillText(String(Math.max(s.score, parseInt(localStorage.getItem(BEST_KEY) || '0'))), W / 2 + 55, H / 2 + 14);

        ctx.fillStyle = '#7c2d12';
        ctx.beginPath();
        ctx.roundRect(W / 2 - 90, H / 2 + 44, 180, 42, 12);
        ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 16px sans-serif';
        ctx.fillText('다시 도전 🔄', W / 2, H / 2 + 70);
      }

      rafRef.current = requestAnimationFrame(loop);
    }

    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [phase, initState]);

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexDirection: 'column', gap: '12px',
      }}
    >
      <button
        onClick={e => { e.stopPropagation(); onClose(); }}
        style={{
          position: 'absolute', top: '16px', right: '20px',
          background: 'rgba(255,255,255,0.2)', border: 'none',
          color: '#fff', fontSize: '22px', cursor: 'pointer',
          borderRadius: '8px', padding: '4px 10px', fontWeight: '700',
          backdropFilter: 'blur(4px)',
        }}>
        ✕
      </button>

      {best > 0 && (
        <div style={{ color: '#fde68a', fontSize: '14px', fontWeight: '700', letterSpacing: '0.5px' }}>
          🏆 최고 기록: {best}
        </div>
      )}

      <canvas
        ref={canvasRef}
        width={W}
        height={H}
        style={{
          borderRadius: '16px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          display: 'block',
          maxWidth: '95vw',
          maxHeight: '80vh',
          objectFit: 'contain',
          touchAction: 'none',
          cursor: ROLLER_CURSOR,
        }}
      />

      <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', marginTop: '4px' }}>
        마우스/터치로 좌우 이동 · ←→ 키도 가능
      </p>
    </div>
  );
}
